import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { FILTER_TYPE, INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "../../constants";
import { SEARCH_MODULES_SUGGESTIONS_DICTIONARY, SORT_ITEM_ORDER } from "../constants";
import { GET_MODULES_SUGGESTIONS } from "./gql";
import { getFilterKey } from "./helpers";

const EXCLUDED_FILTER_FIELDS = ["timestamp"];

const useModulesFilters = () => {
  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection: INITIAL_SORT_DIRECTION,
    initialSortOption: INITIAL_SORT_OPTION,
  });

  const { data, loading, refetch, called } = useQuery(GET_MODULES_SUGGESTIONS, {
    variables: {
      input: {
        fields: null,
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
      },
    },
    skip: !filterVariablesLoaded,
  });

  const cachedFiltersData = useCachedFilterFields(data?.searchModulesSuggestions?.fields);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: SEARCH_MODULES_SUGGESTIONS_DICTIONARY,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
  });

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter(
          (field) =>
            field.orderable && field.name !== "slug" && SORT_ITEM_ORDER.includes(field.name)
        )
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        })
        .sort((a, b) => SORT_ITEM_ORDER.indexOf(a.value) - SORT_ITEM_ORDER.indexOf(b.value)) || []
    );
  }, [cachedFiltersData]);

  const refreshSuggestions = useCallback(
    (fields?: string[]) => {
      if (!called) {
        refetch?.({
          input: {
            fields: fields || null,
            fullTextSearch: filterVariables.searchInput,
            predicates: filterVariables.predicates,
          },
        });
      }
    },
    [filterVariables.searchInput, filterVariables.predicates, called, refetch]
  );

  return {
    filters,
    filtersMap: filterOptionsMap,
    refetch,
    loading,
    itemsCount: data?.searchModulesSuggestions.filteredCount || 0,
    sortOptions,
    refreshSuggestions,
  };
};

export default useModulesFilters;
